import { Head, Link, router, useForm } from "@inertiajs/react"
import camelcaseKeys from "camelcase-keys"
import {
  AlertTriangle,
  ArrowLeft,
  Eye,
  FileText,
  Globe,
  Headphones,
  Loader2,
  Play,
  Save,
  Volume2,
} from "lucide-react"
import { useCallback, useMemo, useRef, useState } from "react"

import { AudioUploader } from "@/components/editorial/AudioUploader"
import { TiptapEditor } from "@/components/editorial/TiptapEditor"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { Phrase } from "@/types/phrase"

interface SourceMessage {
  id: number
  content: string
  audioUrl?: string
}

interface Props {
  phrase: Phrase
  sourceMessage?: SourceMessage
}

export default function EditorialPhraseEdit(props: unknown) {
  const data = useMemo(
    () => camelcaseKeys(props as Record<string, unknown>, { deep: true }) as Props,
    [props]
  )

  const { phrase, sourceMessage } = data
  const [isPlayingSource, setIsPlayingSource] = useState(false)
  const [isPlayingPhrase, setIsPlayingPhrase] = useState(false)
  const sourceAudioRef = useRef<HTMLAudioElement>(null)
  const phraseAudioRef = useRef<HTMLAudioElement>(null)

  // Helper to extract text from potentially nested object
  // Backend may send { text: "..." } or plain string
  const extractText = (value: unknown): string => {
    if (!value) return ""
    if (typeof value === "string") return value
    if (typeof value === "object" && value !== null && "text" in value) {
      return (value as { text: string }).text || ""
    }
    return ""
  }

  // Track if content changed (for audio mismatch warning)
  const [contentChanged, setContentChanged] = useState(false)
  const [editorsAudioFile, setEditorsAudioFile] = useState<File | null>(null)
  const [removeExistingAudio, setRemoveExistingAudio] = useState(false)

  const { data: formData, setData, processing, errors } = useForm({
    content: phrase.content || "",
    thaiText: extractText(phrase.thaiText),
    romanization: extractText(phrase.romanization),
    editorialNote: phrase.editorialNote || "",
  })

  const handleSubmit = (e: React.FormEvent, action: "save" | "publish") => {
    e.preventDefault()

    // Build form data with file if present
    const submitData = {
      phrase: {
        content: formData.content,
        thai_text: formData.thaiText,
        romanization: formData.romanization,
        editorial_note: formData.editorialNote,
        editors_audio: editorsAudioFile,
        remove_editors_audio: removeExistingAudio && !editorsAudioFile,
      }
    }

    if (action === "publish") {
      router.post(`/editorial/phrases/${phrase.slug}/publish`, submitData, {
        preserveScroll: true,
        forceFormData: true,
      })
    } else {
      router.put(`/editorial/phrases/${phrase.slug}`, submitData, {
        preserveScroll: true,
        forceFormData: true,
      })
    }
  }

  const handleContentChange = (value: string) => {
    setData("content", value)
    if (value !== phrase.content && sourceMessage?.audioUrl) {
      setContentChanged(true)
    } else {
      setContentChanged(false)
    }
  }

  // Audio playback helpers
  const toggleSourceAudio = useCallback(() => {
    if (!sourceAudioRef.current) return
    if (isPlayingSource) {
      sourceAudioRef.current.pause()
    } else {
      sourceAudioRef.current.play()
    }
  }, [isPlayingSource])

  const togglePhraseAudio = useCallback(() => {
    if (!phraseAudioRef.current) return
    if (isPlayingPhrase) {
      phraseAudioRef.current.pause()
    } else {
      phraseAudioRef.current.play()
    }
  }, [isPlayingPhrase])

  const isPublished = phrase.status === "published"
  const canPublish = formData.content && formData.thaiText && formData.romanization

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Editorial", href: "/editorial" },
        { title: "Phrases", href: "/editorial/phrases" },
        { title: "Edit", href: `/editorial/phrases/${phrase.slug}/edit` },
      ]}
    >
      <Head title={`Edit: ${phrase.content}`} />

      {/* Hidden audio elements */}
      {sourceMessage?.audioUrl && (
        <audio
          ref={sourceAudioRef}
          src={sourceMessage.audioUrl}
          onPlay={() => setIsPlayingSource(true)}
          onPause={() => setIsPlayingSource(false)}
          onEnded={() => setIsPlayingSource(false)}
        />
      )}
      {phrase.audioUrl && (
        <audio
          ref={phraseAudioRef}
          src={phrase.audioUrl}
          onPlay={() => setIsPlayingPhrase(true)}
          onPause={() => setIsPlayingPhrase(false)}
          onEnded={() => setIsPlayingPhrase(false)}
        />
      )}

      <div className="mx-auto max-w-3xl space-y-6 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/editorial/phrases">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Edit Phrase
              </h1>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {isPublished ? "Edit published phrase" : "Edit draft and publish when ready"}
              </p>
            </div>
          </div>

          <Badge
            variant="secondary"
            className={cn(
              isPublished
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
            )}
          >
            {isPublished ? "Published" : "Draft"}
          </Badge>
        </div>

        {/* Audio Mismatch Warning */}
        {contentChanged && (
          <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              You changed the content. The audio may no longer match the text.
              Consider updating the audio or reverting the text change.
            </AlertDescription>
          </Alert>
        )}

        {/* Source Message Reference */}
        {sourceMessage && (
          <Card className="border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Source Message
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-lg text-foreground">{sourceMessage.content}</p>
              {sourceMessage.audioUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleSourceAudio}
                  className="gap-2"
                >
                  {isPlayingSource ? (
                    <Volume2 className="h-4 w-4 animate-pulse" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {isPlayingSource ? "Playing..." : "Play Original Audio"}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Edit Form */}
        <form onSubmit={(e) => handleSubmit(e, "save")}>
          <Card>
            <CardHeader>
              <CardTitle>Phrase Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* English Content */}
              <div className="space-y-2">
                <Label htmlFor="content">
                  English Phrase <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="content"
                  value={formData.content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder="e.g., Can you speak English?"
                  className={errors.content ? "border-red-500" : ""}
                />
                {errors.content && (
                  <p className="text-sm text-red-500">{errors.content}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  This becomes the page title and URL slug
                </p>
              </div>

              {/* Thai Text */}
              <div className="space-y-2">
                <Label htmlFor="thaiText">
                  Thai Translation <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="thaiText"
                  value={formData.thaiText}
                  onChange={(e) => setData("thaiText", e.target.value)}
                  placeholder="e.g., คุณพูดภาษาอังกฤษได้ไหม"
                  className={cn(
                    "min-h-[80px] text-2xl",
                    errors.thaiText ? "border-red-500" : ""
                  )}
                />
                {errors.thaiText && (
                  <p className="text-sm text-red-500">{errors.thaiText}</p>
                )}
              </div>

              {/* Romanization */}
              <div className="space-y-2">
                <Label htmlFor="romanization">
                  Romanization <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="romanization"
                  value={formData.romanization}
                  onChange={(e) => setData("romanization", e.target.value)}
                  placeholder="e.g., khun phûut phaa-sǎa ang-grìt dâi mái"
                  className={errors.romanization ? "border-red-500" : ""}
                />
                {errors.romanization && (
                  <p className="text-sm text-red-500">{errors.romanization}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Pronunciation guide for learners
                </p>
              </div>

              {/* Slug Preview */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">URL Preview</Label>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900">
                  <span className="text-muted-foreground">thaicopilot.com/phrases/</span>
                  <span className="font-medium text-foreground">
                    {formData.content
                      .toLowerCase()
                      .replace(/[^a-z0-9\s-]/g, "")
                      .replace(/\s+/g, "-")
                      .substring(0, 50) || "your-phrase-here"}
                  </span>
                </div>
                {isPublished && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Note: Slug is locked after publishing to protect SEO
                  </p>
                )}
              </div>

              {/* Audio Status */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">Audio</Label>
                {phrase.audioUrl ? (
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={togglePhraseAudio}
                      className="gap-2"
                    >
                      {isPlayingPhrase ? (
                        <Volume2 className="h-4 w-4 animate-pulse" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {isPlayingPhrase ? "Playing..." : "Play Audio"}
                    </Button>
                    <span className="text-sm text-emerald-600 dark:text-emerald-400">
                      Audio ready
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Audio will be copied from source message when published
                  </p>
                )}
              </div>

              <Separator />

              {/* Editorial Content Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-lg font-medium text-slate-700 dark:text-slate-300">
                  <FileText className="h-5 w-5" />
                  Editorial Content
                </div>

                {/* Editorial Note */}
                <div className="space-y-2">
                  <Label htmlFor="editorialNote">Editorial Note</Label>
                  <p className="text-xs text-muted-foreground">
                    Add cultural context, usage tips, or personal insights about this phrase
                  </p>
                  <div className="relative">
                    <TiptapEditor
                      content={formData.editorialNote}
                      onChange={(html) => setData("editorialNote", html)}
                      placeholder="Share when and why you use this phrase, cultural nuances, or tips for learners..."
                    />
                  </div>
                </div>

                {/* Editor's Audio */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Headphones className="h-4 w-4 text-muted-foreground" />
                    <Label>Editor's Audio</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Record or upload an audio explanation of the phrase
                  </p>
                  <AudioUploader
                    value={editorsAudioFile}
                    existingUrl={!removeExistingAudio ? phrase.editorsAudioUrl : undefined}
                    onChange={setEditorsAudioFile}
                    onRemoveExisting={() => setRemoveExistingAudio(true)}
                  />
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Button
                  type="submit"
                  variant="outline"
                  disabled={processing}
                  className="gap-2"
                >
                  {processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Draft
                </Button>

                <div className="flex gap-3">
                  {isPublished && (
                    <Button
                      type="button"
                      variant="outline"
                      asChild
                      className="gap-2"
                    >
                      <Link href={`/phrases/${phrase.slug}`} target="_blank">
                        <Eye className="h-4 w-4" />
                        View Public Page
                      </Link>
                    </Button>
                  )}

                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, "publish")}
                    disabled={processing || !canPublish}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Globe className="h-4 w-4" />
                    )}
                    {isPublished ? "Update & Republish" : "Publish"}
                  </Button>
                </div>
              </div>

              {!canPublish && (
                <p className="text-center text-sm text-muted-foreground">
                  Fill in all required fields to publish
                </p>
              )}
            </CardContent>
          </Card>
        </form>
      </div>
    </AppLayout>
  )
}
